import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { meApi } from '../services/api';
import { useAuth } from '../store/auth-context';
import { usePlatformEvents } from '../contexts/PlatformEventContext';

/**
 * The list of spaces the signed-in user belongs to. Fed to the space switcher
 * and used by `SpaceProvider` to validate that the URL `:spaceId` segment
 * resolves to a space the user can actually access.
 *
 * Subscribes to `optr.space.*` WebSocket events so the list reacts to:
 *   - someone archiving / unarchiving a space the user belongs to
 *   - the user's role changing (member → admin, etc.)
 *   - the user being added to or removed from a space
 *   - ownership transfers
 *
 * If the user is removed from the space they're currently viewing, the
 * defensive redirect bounces them to `/c` so `ConsoleDefaultSpaceRedirect`
 * picks the next available space.
 */
export function useMeSpaces(opts?: { includeArchived?: boolean }) {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { subscribe } = usePlatformEvents();
  const includeArchived = !!opts?.includeArchived;

  const query = useQuery({
    queryKey: ['me', 'spaces', { includeArchived }],
    queryFn: () => meApi.spaces({ includeArchived }),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    // Only the events that change *which spaces this user can access* are worth
    // a live subscription: being added to / removed from a space (the latter
    // also drives the defensive redirect below). Rarer metadata changes
    // (archived/unarchived, role-changed, ownership-transferred) are picked up
    // by the query's staleTime + refetch-on-navigation rather than a standing
    // WS filter, keeping the always-on subscription set lean.
    const unsub = subscribe(
      [
        { eventType: 'optr.space.member.added' },
        { eventType: 'optr.space.member.removed' },
      ],
      (event) => {
        // Refetch every variant of the spaces list (with / without archived).
        queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });

        // Defensive redirect: if the user themselves was removed from a
        // space they're currently viewing, bounce out of it.
        if (event.eventType === 'optr.space.member.removed') {
          const data = event.data as { userId?: string; spaceId?: string };
          if (data.userId && user?.userId && data.userId === user.userId) {
            const path = window.location.pathname;
            if (data.spaceId && path.includes(`/c/s/${data.spaceId}/`)) {
              navigate('/c', { replace: true });
            }
          }
        }
      },
    );
    return unsub;
  }, [isAuthenticated, subscribe, queryClient, navigate, user?.userId]);

  return query;
}
